package com.karyadina.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items

import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier

import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.PomodoroSession
import com.karyadina.model.SessionType
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.PomodoroServiceImpl
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.util.currentTimeMillis
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PomodoroHistoryScreen(
    service: PomodoroServiceImpl,
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    var sessions by remember { mutableStateOf(service.getSessionHistory(50)) }
    var selectedFilter by remember { mutableStateOf("All") }
    var showDeleteDialog by remember { mutableStateOf(false) }
    
    val filteredSessions = remember(sessions, selectedFilter) {
        when (selectedFilter) {
            "Focus" -> sessions.filter { it.sessionType == SessionType.FOCUS.name }
            "Breaks" -> sessions.filter { it.sessionType != SessionType.FOCUS.name }
            "Completed" -> sessions.filter { it.completed }
            else -> sessions
        }
    }
    
    val groupedSessions = remember(filteredSessions) {
        groupSessionsByDate(filteredSessions)
    }
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text(stringResource(StudyStrings.HistoryTitle), style = MaterialTheme.typography.titleLarge)
                        Text(
                            stringResource(StudyStrings.HistorySubtitle, sessions.size),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(0.7f)
                        )
                    }
                },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, "Back")
                    }
                },
                actions = {
                    if (sessions.isNotEmpty()) {
                        IconButton(onClick = { showDeleteDialog = true }) {
                            Icon(Icons.Outlined.DeleteSweep, stringResource(StudyStrings.HistoryClearAll))
                        }
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.surface,
                    titleContentColor = MaterialTheme.colorScheme.onSurface
                )
            )
        }
    ) { padding ->
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(padding)
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.surface,
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                        )
                    )
                )
        ) {
            // Filter chips
            Row(
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                listOf(
                    stringResource(StudyStrings.NotesFilterAll),
                    stringResource(StudyStrings.HistoryFilterFocus),
                    stringResource(StudyStrings.HistoryFilterBreaks),
                    stringResource(StudyStrings.PlannerSectionCompleted)
                ).forEach { filter ->
                    FilterChip(
                        selected = selectedFilter == filter,
                        onClick = { selectedFilter = filter },
                        label = { Text(filter) },
                        leadingIcon = if (selectedFilter == filter) {
                            { Icon(Icons.Filled.Check, null, Modifier.size(16.dp)) }
                        } else null
                    )
                }
            }
            
            if (filteredSessions.isEmpty()) {
                EmptyHistoryState()
            } else {
                LazyColumn(
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    groupedSessions.forEach { (date, sessionsInDay) ->
                        item(key = "header_$date") {
                            DateHeader(date, sessionsInDay.size)
                        }
                        
                        items(sessionsInDay, key = { it.id }) { session ->
                            SessionHistoryCard(session)
                        }
                    }
                }
            }
        }
        
        if (showDeleteDialog) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                title = { Text(stringResource(StudyStrings.HistoryDialogClearTitle)) },
                text = { Text(stringResource(StudyStrings.HistoryDialogClearMsg, sessions.size)) },
                confirmButton = {
                    TextButton(
                        onClick = {
                            // Clear history from service and storage
                            service.clearHistory()
                            sessions = emptyList()
                            showDeleteDialog = false
                        },
                        colors = ButtonDefaults.textButtonColors(
                            contentColor = MaterialTheme.colorScheme.error
                        )
                    ) {
                        Text(stringResource(StudyStrings.TimerBtnStop)) // Using same label as stop for simplicity or add Clear All resource
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = false }) {
                        Text(stringResource(StudyStrings.Cancel))
                    }
                }
            )
        }
    }
}

@Composable
fun DateHeader(date: String, sessionCount: Int) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            date,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
        Surface(
            shape = MaterialTheme.shapes.small,
            color = MaterialTheme.colorScheme.primaryContainer
        ) {
            Text(
                "$sessionCount sessions",
                modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onPrimaryContainer
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SessionHistoryCard(session: PomodoroSession) {
    val sessionColor = when (SessionType.valueOf(session.sessionType)) {
        SessionType.FOCUS -> MaterialTheme.colorScheme.primaryContainer
        SessionType.SHORT_BREAK -> MaterialTheme.colorScheme.secondaryContainer
        SessionType.LONG_BREAK -> MaterialTheme.colorScheme.tertiaryContainer
    }
    
    val statusIcon = when {
        session.completed -> Icons.Filled.CheckCircle
        session.interrupted -> Icons.Filled.Cancel
        else -> Icons.Filled.Circle
    }
    
    val statusColor = when {
        session.completed -> Color(0xFF4CAF50) // Green
        session.interrupted -> MaterialTheme.colorScheme.error
        else -> MaterialTheme.colorScheme.outline
    }
    
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = sessionColor
        )
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Session icon and type
            Row(
                Modifier.weight(1f),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape,
                    color = MaterialTheme.colorScheme.surface.copy(alpha = 0.5f)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(
                            when (SessionType.valueOf(session.sessionType)) {
                                SessionType.FOCUS -> Icons.Filled.Timer
                                SessionType.SHORT_BREAK -> Icons.Filled.Coffee
                                SessionType.LONG_BREAK -> Icons.Filled.BeachAccess
                            },
                            contentDescription = null,
                            modifier = Modifier.size(24.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }
                }
                
                Column {
                    Text(
                        formatSessionType(session.sessionType),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    if (session.task != null) {
                        Text(
                            session.task,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(0.7f)
                        )
                    }
                    Text(
                        formatTime(session.startTime),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(0.6f)
                    )
                }
            }
            
            // Duration and status
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        statusIcon,
                        contentDescription = null,
                        modifier = Modifier.size(16.dp),
                        tint = statusColor
                    )
                    Text(
                        "${session.durationMinutes}min",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                }
                
                if (session.completed) {
                    Surface(
                        shape = MaterialTheme.shapes.extraSmall,
                        color = Color(0xFF4CAF50).copy(alpha = 0.2f)
                    ) {
                        Text(
                            stringResource(StudyStrings.TimerSessionFocus), // Mapping Completed to Focus or similar, or add new resource
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            style = MaterialTheme.typography.labelSmall,
                            color = Color(0xFF2E7D32)
                        )
                    }
                } else if (session.interrupted) {
                    Surface(
                        shape = MaterialTheme.shapes.extraSmall,
                        color = MaterialTheme.colorScheme.errorContainer
                    ) {
                        Text(
                            "Interrupted", // Add resource if needed
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onErrorContainer
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun EmptyHistoryState() {
    Box(
        Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Icon(
                Icons.Outlined.History,
                contentDescription = null,
                modifier = Modifier.size(80.dp),
                tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f)
            )
            Text(
                stringResource(StudyStrings.HistoryEmptyTitle),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold
            )
            Text(
                stringResource(StudyStrings.HistoryEmptyMsg),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface.copy(0.7f),
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
        }
    }
}

fun groupSessionsByDate(sessions: List<PomodoroSession>): Map<String, List<PomodoroSession>> {
    val now = currentTimeMillis()
    val oneDayMs = 24 * 60 * 60 * 1000L
    
    return sessions.groupBy { session ->
        val daysDiff = ((now - session.startTime) / oneDayMs).toInt()
        when {
            daysDiff == 0 -> "Today" // Resources can't be used outside of Composable easily, this helper might need refactoring or pass strings in
            daysDiff == 1 -> "Yesterday"
            daysDiff < 7 -> "$daysDiff days ago"
            daysDiff < 30 -> "${daysDiff / 7} weeks ago"
            else -> "${daysDiff / 30} months ago"
        }
    }
}

@Composable
fun formatSessionType(type: String): String {
    return when (type) {
        SessionType.FOCUS.name -> stringResource(StudyStrings.TimerSessionFocus)
        SessionType.SHORT_BREAK.name -> stringResource(StudyStrings.TimerSessionShortBreak)
        SessionType.LONG_BREAK.name -> stringResource(StudyStrings.TimerSessionLongBreak)
        else -> type
    }
}

fun formatTime(timestamp: Long): String {
    val date = timestamp // In real app, format this properly
    val hours = ((timestamp / 1000 / 60 / 60) % 24).toInt()
    val minutes = ((timestamp / 1000 / 60) % 60).toInt()
    return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
}
